import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "@/lib/api";
import {
  ShieldCheck, ArrowRight, CheckCircle, FileArrowDown, PlugsConnected, LinkSimple,
} from "@phosphor-icons/react";

const PLATFORMS = [
  { id: "infor",       name: "Infor",              tag: "TG4 · SunSystems · M3 · ION",  sector: "Public sector, healthcare",     color: "#D50037", highlight: true,  sample: "infor" },
  { id: "flowforma",   name: "FlowForma",          tag: "Power Automate · SharePoint",  sector: "Irish public sector · HSE",     color: "#00B67A", highlight: true,  sample: "flowforma" },
  { id: "sap",         name: "SAP Concur & Ariba", tag: "Concur · Ariba · S/4HANA",     sector: "Large enterprises · universities", color: "#0FAAFF", highlight: true, sample: "sap-concur" },
  { id: "netsuite",    name: "Oracle NetSuite",    tag: "NetSuite · Oracle Cloud",      sector: "Universities · corporates",     color: "#F80000",                    sample: "netsuite" },
  { id: "workday",     name: "Workday",            tag: "HCM · Financials",             sector: "Universities · large orgs",     color: "#F38B00",                    sample: "workday" },
  { id: "sage",        name: "Sage",               tag: "Sage 50 · 200 · Business Cloud",sector: "SMEs · start-ups",             color: "#00DC00",                    sample: "sage" },
  { id: "xero",        name: "Xero",               tag: "Bills · Bank feeds · API",     sector: "Irish start-ups & spin-outs",   color: "#13B5EA", highlight: true, sample: "xero" },
  { id: "quickbooks",  name: "QuickBooks",         tag: "Online · Desktop",             sector: "SMEs",                          color: "#2CA01C",                   sample: "quickbooks" },
  { id: "unit4",       name: "Unit4 Business World",tag: "Agresso platform",            sector: "Public sector · education",     color: "#E4002B",                    sample: "unit4" },
  { id: "agresso",     name: "Agresso / CGI",      tag: "Local authorities",            sector: "Local authorities · universities", color: "#0057B8",                sample: "agresso" },
];

const HIGHLIGHTS = [
  { title: "10 export formats", body: "Column-matched CSVs for every major ERP, spanning Irish public sector to global enterprise." },
  { title: "Grant body templates", body: "Enterprise Ireland, Horizon Europe and EIT Health templates included." },
  { title: "Machine-to-machine", body: "REST API + HMAC-signed webhooks for real-time syncs via Zapier, Make, or Power Automate." },
  { title: "GDPR audit-logged", body: "Every export is recorded with timestamp and audit hash. Data stays in the EU region." },
];

export default function Compatibility() {
  return (
    <div className="min-h-screen aurora grain">
      <header className="hairline-b">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center gold-glow" style={{ background: "#FFB020" }}>
              <ShieldCheck size={22} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div className="font-display text-lg leading-none">PerCen360</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-1">Compatibility Pack</div>
            </div>
          </Link>
          <Link to="/" className="btn-gold text-sm">Try PerCen360 <ArrowRight size={14} weight="bold"/></Link>
        </div>
      </header>

      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 py-20">
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="max-w-3xl">
          <div className="eyebrow mb-4">Interoperability · Ireland · EU</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] tracking-tight">
            PerCen360 exports directly to <span style={{ color: "#FFB020" }}>Infor</span>, <span style={{ color: "#FFB020" }}>FlowForma</span>, <span style={{ color: "#FFB020" }}>SAP</span> and every major Irish public-sector finance system.
          </h1>
          <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">
            Grant reviewers no longer have to ask &quot;does your system talk to ours?&quot; — the answer is yes.
            Every receipt and grant expense can be pushed to the platform of your choice, in the exact CSV shape the vendor expects.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {HIGHLIGHTS.map((h,i) => (
            <motion.div key={h.title}
              initial={{opacity:0,y:12}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5,delay:i*0.06}}
              className="glass rounded-xl p-5">
              <CheckCircle size={20} weight="fill" color="#00A86B" className="mb-3"/>
              <div className="font-display text-base">{h.title}</div>
              <div className="text-white/60 text-xs mt-2 leading-relaxed">{h.body}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 pb-24">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-2">Supported platforms</div>
            <h2 className="font-display text-3xl">Sample CSVs — no login required</h2>
            <p className="text-white/55 text-sm mt-2 max-w-2xl">Download a sample row from each format to preview column shape and verify import compatibility with your finance team.</p>
          </div>
          <div className="flex items-center gap-2 glass rounded-full px-4 py-2 text-xs">
            <PlugsConnected size={14} weight="duotone" color="#FFB020"/>
            <span className="text-white/70">Live sync API + webhooks available for paid tiers</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLATFORMS.map((p, i) => (
            <motion.div key={p.id}
              initial={{opacity:0, y:12}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.4, delay: i*0.03}}
              className="glass rounded-xl p-5 flex flex-col gap-4" data-testid={`compat-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg font-display font-bold text-xl flex items-center justify-center shrink-0"
                    style={{ background: `${p.color}18`, border: `1px solid ${p.color}55`, color: p.color }}>
                    {p.name[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-base leading-tight">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/45 mt-1">{p.tag}</div>
                  </div>
                </div>
                {p.highlight && (
                  <span className="badge-warn px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold">Priority</span>
                )}
              </div>
              <div className="text-white/60 text-xs">{p.sector}</div>
              <div className="flex gap-2 mt-auto">
                <Link to={`/integrations/${p.id}`} className="btn-ghost text-xs justify-center flex-1" data-testid={`compat-details-${p.id}`}>
                  Learn more <ArrowRight size={12}/>
                </Link>
                <a
                  href={`${API}/public/exports/sample/${p.sample}.csv`}
                  className="btn-ghost text-xs justify-center"
                  data-testid={`compat-sample-${p.id}`}
                  title="Download sample CSV"
                >
                  <FileArrowDown size={14}/>
                </a>
                <a
                  href={`${API}/public/one-pager/${p.id}.pdf`}
                  target="_blank" rel="noreferrer"
                  className="btn-ghost text-xs justify-center"
                  data-testid={`compat-pdf-${p.id}`}
                  title="Download PDF one-pager"
                >
                  <FileArrowDown size={14} weight="fill"/>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 sm:px-10 pb-24">
        <div className="glass rounded-2xl p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="eyebrow mb-3">Include in your grant application</div>
            <h2 className="font-display text-3xl sm:text-4xl max-w-2xl leading-tight">Copy-paste line for Enterprise Ireland / Horizon Europe applications</h2>
            <div className="mt-6 rounded-xl bg-black/40 border border-white/10 p-5 mono text-sm text-white/80 leading-relaxed">
              &quot;Financial governance handled by <span style={{ color: "#FFB020" }}>PerCen360</span> — GDPR-native compliance OS with direct exports to Infor, FlowForma, SAP, Oracle NetSuite, Sage, Xero, Unit4/Agresso and other Irish public-sector finance systems. Every transaction is audit-hashed and Revenue.ie rule-checked.&quot;
            </div>
          </div>
          <Link to="/" className="btn-gold text-base shrink-0">
            Start free <ArrowRight size={16} weight="bold"/>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/50">
          <div className="flex items-center gap-3">
            <ShieldCheck size={16} weight="duotone" color="#FFB020"/>
            © {new Date().getFullYear()} PerCen Technologies Ltd · percen360.com · EU region
          </div>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/dpia" className="hover:text-white">DPIA</Link>
            <a href="mailto:gillian@percen360.com" className="hover:text-white flex items-center gap-1"><LinkSimple size={11}/> Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
